<script setup>
// NOTICE: KAPTION-INTEGRATION
// contact-first-conversation.SEARCH / NEW_CONTACT / INBOX / RESOLVE —
// "Nuevo mensaje", step 1 and only step: choose the contact (search by name or
// phone, or paste a new number and create it), pick the WhatsApp inbox when
// there are several, then open the contact's conversation in that inbox, or an
// empty draft conversation when there is none. Nothing is created until the
// agent sends (see KaptionDraftConversation).
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { debounce } from '@chatwoot/utils';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import ContactAPI from 'dashboard/api/contacts';
import { getNewConversationDefaults } from 'dashboard/api/kaptionNewConversation';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import KaptionFilterSelect from 'dashboard/components-next/Campaigns/KaptionFilterSelect.vue';
import {
  createNewContact,
  searchContacts,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import {
  looksLikePhoneNumber,
  normalizeToE164,
  deriveDefaultCountry,
} from 'dashboard/components-next/NewConversationModal/helpers/newConversationHelpers';
import {
  DRAFT_ROUTE_NAME,
  phoneToSourceId,
  pickExistingConversation,
  reachableContacts,
  readLastUsedInboxId,
  resolveDefaultInbox,
  samePhone,
  writeLastUsedInboxId,
} from './kaptionContactFirstHelpers';

defineProps({
  canOpenAdvanced: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'advanced']);

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const accountId = useMapGetter('getCurrentAccountId');
const getWhatsAppInboxes = useMapGetter('inboxes/getWhatsAppInboxes');

const inputRef = ref(null);
const query = ref('');
const results = ref([]);
const isSearching = ref(false);
const isOpening = ref(false);
const newContactName = ref('');
const selectedInboxId = ref(null);
const inboxTouched = ref(false);
const country = ref('');
const preferredInboxId = ref(null);
let searchSequence = 0;

const inboxes = computed(() => getWhatsAppInboxes.value || []);
const selectedInbox = computed(
  () => inboxes.value.find(inbox => inbox.id === selectedInboxId.value) || null
);
const inboxOptions = computed(() =>
  inboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const isNumber = computed(() => looksLikePhoneNumber(query.value));
const parsedNumber = computed(() =>
  isNumber.value
    ? normalizeToE164(
        query.value,
        country.value || deriveDefaultCountry(selectedInbox.value)
      )
    : { e164: '', isValid: false, national: '' }
);
// contact-first-conversation.NEW_CONTACT.1 — offer a new contact only for a
// valid number that no found contact already has (no duplicate contacts).
const showNewContact = computed(
  () =>
    isNumber.value &&
    parsedNumber.value.isValid &&
    !isSearching.value &&
    !results.value.some(contact =>
      samePhone(contact.phoneNumber, parsedNumber.value.e164)
    )
);
const newContactPhoneLabel = computed(
  () =>
    parsePhoneNumberFromString(
      parsedNumber.value.e164 || ''
    )?.formatInternational() || parsedNumber.value.e164
);
const showIncompleteNumber = computed(
  () => isNumber.value && !parsedNumber.value.isValid && query.value.length > 3
);

// contact-first-conversation.SEARCH.1 — name or phone; stale answers dropped.
const runSearch = debounce(async (value, sequence) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    results.value = [];
    isSearching.value = false;
    return;
  }
  try {
    const found = await searchContacts({
      keys: ['name', 'phone_number'],
      query: looksLikePhoneNumber(trimmed)
        ? trimmed.replace(/\D/g, '').slice(-8)
        : trimmed,
    });
    if (sequence === searchSequence) {
      results.value = reachableContacts(found).slice(0, 8);
    }
  } catch {
    if (sequence === searchSequence) results.value = [];
  } finally {
    if (sequence === searchSequence) isSearching.value = false;
  }
}, 250);

const onInput = () => {
  searchSequence += 1;
  isSearching.value = Boolean(query.value.trim());
  runSearch(query.value, searchSequence);
};

const chooseInbox = inboxId => {
  selectedInboxId.value = Number(inboxId);
  inboxTouched.value = true;
};

const fetchContactConversations = async contactId => {
  try {
    const { data } = await ContactAPI.getConversations(contactId);
    return data?.payload || [];
  } catch {
    return [];
  }
};

const openFor = async (contact, conversations) => {
  const inbox = inboxTouched.value
    ? selectedInbox.value
    : resolveDefaultInbox({
        inboxes: inboxes.value,
        contactConversations: conversations,
        lastUsedInboxId: readLastUsedInboxId(),
        preferredInboxId: preferredInboxId.value,
      });
  if (!inbox) return;
  writeLastUsedInboxId(inbox.id);
  // contact-first-conversation.RESOLVE.1 — existing conversation first.
  const existing = pickExistingConversation(conversations, inbox.id);
  emit('close');
  if (existing) {
    router.push({
      name: 'inbox_conversation',
      params: { accountId: accountId.value, conversation_id: existing.id },
    });
    return;
  }
  // contact-first-conversation.RESOLVE.2 — otherwise the empty draft.
  router.push({
    name: DRAFT_ROUTE_NAME,
    params: {
      accountId: accountId.value,
      contactId: contact.id,
      inboxId: inbox.id,
    },
  });
};

const selectContact = async contact => {
  if (isOpening.value) return;
  isOpening.value = true;
  try {
    const conversations = await fetchContactConversations(contact.id);
    await openFor(contact, conversations);
  } finally {
    isOpening.value = false;
  }
};

// contact-first-conversation.NEW_CONTACT.2 — create the contact (name
// optional, the number otherwise) and open its empty conversation.
const createAndOpen = async () => {
  if (isOpening.value || !showNewContact.value || !selectedInbox.value) return;
  isOpening.value = true;
  try {
    const { e164 } = parsedNumber.value;
    const contact = await createNewContact({
      phoneNumber: e164,
      firstName: newContactName.value.trim() || undefined,
      inboxId: selectedInbox.value.id,
      sourceId: phoneToSourceId(e164),
    });
    inboxTouched.value = true;
    await openFor(contact, []);
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('KAPTION_CONTACT_FIRST.PALETTE.CREATE_ERROR')
    );
  } finally {
    isOpening.value = false;
  }
};

const onEnter = () => {
  if (results.value.length) selectContact(results.value[0]);
  else if (showNewContact.value) createAndOpen();
};

onMounted(async () => {
  nextTick(() => inputRef.value?.focus());
  if (!inboxes.value.length) await store.dispatch('inboxes/get');
  const lastUsed = readLastUsedInboxId();
  selectedInboxId.value =
    inboxes.value.find(inbox => inbox.id === lastUsed)?.id ||
    inboxes.value[0]?.id ||
    null;
  try {
    const { data } = await getNewConversationDefaults(accountId.value);
    country.value = data.country || '';
    preferredInboxId.value = data.preferred_inbox_id || null;
    if (!lastUsed && inboxes.value.some(i => i.id === data.preferred_inbox_id))
      selectedInboxId.value = data.preferred_inbox_id;
  } catch {
    // the inbox number still gives a country
  }
});

useKeyboardEvents({
  Escape: { action: () => emit('close'), allowOnFocusedInput: true },
});
</script>

<template>
  <teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh] bg-n-alpha-black2 backdrop-blur-[6px]"
      data-testid="contact-first-palette"
      @click.self="emit('close')"
    >
      <div
        class="w-[34rem] max-w-full flex flex-col overflow-hidden bg-n-alpha-3 border border-n-strong shadow-2xl backdrop-blur-[100px] rounded-2xl"
      >
        <div class="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
          <div class="min-w-0">
            <h2 class="text-base font-semibold leading-tight text-n-slate-12">
              {{ t('KAPTION_CONTACT_FIRST.PALETTE.TITLE') }}
            </h2>
            <p class="text-xs leading-tight text-n-slate-10">
              {{ t('KAPTION_CONTACT_FIRST.PALETTE.SUBTITLE') }}
            </p>
          </div>
          <button
            type="button"
            class="flex items-center justify-center rounded-lg size-8 shrink-0 text-n-slate-11 hover:bg-n-alpha-black2"
            :aria-label="t('KAPTION_CONTACT_FIRST.PALETTE.CLOSE')"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="px-5 pb-3">
          <div
            class="flex items-center gap-2 px-3 h-10 rounded-xl bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand"
          >
            <span class="i-lucide-search size-4 text-n-slate-10 shrink-0" />
            <input
              ref="inputRef"
              v-model="query"
              type="text"
              data-testid="contact-first-search"
              class="reset-base flex-1 min-w-0 text-sm bg-transparent border-0 outline-none text-n-slate-12 placeholder:text-n-slate-10"
              :placeholder="t('KAPTION_CONTACT_FIRST.PALETTE.SEARCH')"
              autocomplete="off"
              @input="onInput"
              @keydown.enter.prevent="onEnter"
            />
            <Spinner v-if="isSearching || isOpening" :size="16" />
          </div>
          <div
            v-if="inboxes.length > 1"
            class="flex items-center gap-2 mt-2 text-xs text-n-slate-11"
            data-testid="contact-first-inbox"
          >
            <span class="shrink-0">
              {{ t('KAPTION_CONTACT_FIRST.PALETTE.FROM_INBOX') }}
            </span>
            <KaptionFilterSelect
              :model-value="selectedInboxId"
              :options="inboxOptions"
              icon="i-ri-whatsapp-line"
              @update:model-value="chooseInbox"
            />
          </div>
        </div>

        <ul
          v-if="results.length"
          class="flex flex-col max-h-80 overflow-y-auto border-t border-n-weak py-1"
        >
          <li v-for="contact in results" :key="contact.id">
            <button
              type="button"
              class="flex items-center w-full gap-3 px-5 py-2 text-left hover:bg-n-alpha-black2 focus-visible:bg-n-alpha-black2 outline-none"
              data-testid="contact-first-result"
              :disabled="isOpening"
              @click="selectContact(contact)"
            >
              <Avatar
                :name="contact.name || contact.phoneNumber"
                :src="contact.thumbnail"
                :size="28"
                rounded-full
              />
              <span class="flex flex-col min-w-0">
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ contact.name || contact.phoneNumber }}
                </span>
                <span class="text-xs truncate text-n-slate-10">
                  {{ contact.phoneNumber }}
                </span>
              </span>
              <span
                class="ml-auto i-lucide-arrow-right size-4 text-n-slate-9 shrink-0"
              />
            </button>
          </li>
        </ul>

        <div
          v-if="showNewContact"
          class="flex flex-col gap-2 px-5 py-3 border-t border-n-weak"
          data-testid="contact-first-new"
        >
          <p class="text-sm text-n-slate-12">
            <span class="i-lucide-user-plus size-4 align-[-3px] mr-1" />
            {{
              t('KAPTION_CONTACT_FIRST.PALETTE.NEW_CONTACT', {
                phone: newContactPhoneLabel,
              })
            }}
          </p>
          <div class="flex items-center gap-2">
            <input
              v-model="newContactName"
              type="text"
              data-testid="contact-first-new-name"
              class="reset-base flex-1 h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak outline-none text-n-slate-12 focus:border-n-brand"
              :placeholder="t('KAPTION_CONTACT_FIRST.PALETTE.NEW_CONTACT_NAME')"
              @keydown.enter.prevent="createAndOpen"
            />
            <button
              type="button"
              data-testid="contact-first-create"
              class="h-9 px-3 text-sm font-medium text-white rounded-lg bg-n-brand hover:brightness-110 disabled:opacity-50"
              :disabled="isOpening"
              @click="createAndOpen"
            >
              {{ t('KAPTION_CONTACT_FIRST.PALETTE.CREATE_AND_OPEN') }}
            </button>
          </div>
        </div>

        <p
          v-else-if="showIncompleteNumber"
          class="px-5 py-3 text-xs border-t border-n-weak text-n-slate-10"
        >
          {{ t('KAPTION_CONTACT_FIRST.PALETTE.INCOMPLETE_NUMBER') }}
        </p>
        <p
          v-else-if="query.trim() && !isSearching && !results.length"
          class="px-5 py-3 text-xs border-t border-n-weak text-n-slate-10"
          data-testid="contact-first-empty"
        >
          {{ t('KAPTION_CONTACT_FIRST.PALETTE.NO_RESULTS') }}
        </p>

        <div
          v-if="canOpenAdvanced"
          class="flex justify-end px-5 py-2 border-t border-n-weak"
        >
          <button
            type="button"
            class="text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
            data-testid="contact-first-advanced"
            @click="emit('advanced')"
          >
            {{ t('KAPTION_CONTACT_FIRST.PALETTE.ADVANCED') }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>
